import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, Zap, Users, Sparkles } from "lucide-react";
import { api, useSeo } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import StoreCard from "@/components/StoreCard";

export default function Home() {
  useSeo(
    "SharePromos — Verified Coupon Codes & Promo Deals for 500+ Stores",
    "Save on Amazon, Nike, Target, Best Buy and more with hand-verified coupon codes, promos, and cashback offers. Updated daily by real editors.",
    {
      keywords: "coupons, coupon codes, promo codes, discount codes, deals, cashback, online shopping, savings, verified promo, free shipping",
      image: "https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?w=1200&q=80",
    }
  );
  const [q, setQ] = useState("");
  const [featured, setFeatured] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ stores: 0, coupons: 0 });
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      // trigger seed if empty
      const s = await api.get("/stats").then((r) => r.data);
      if (s.coupons === 0) await api.post("/seed");
      const [fc, fs, cats, stats2] = await Promise.all([
        api.get("/coupons", { params: { featured: true, limit: 8 } }),
        api.get("/stores", { params: { featured: true, limit: 12 } }),
        api.get("/categories"),
        api.get("/stats"),
      ]);
      setFeatured(fc.data);
      setStores(fs.data);
      setCategories(cats.data);
      setStats(stats2.data);
    })().catch(console.error);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-slate-200 bg-white relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 paper-grid opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 relative">
          <div className="max-w-4xl">
            <p className="overline mb-6 text-emerald-600" data-testid="hero-eyebrow">
              — Verified daily · {stats.coupons.toLocaleString()}+ live deals
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] uppercase mb-8">
              Coupons that <span className="text-emerald-500">actually</span> work.
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed mb-10">
              We test every code before it goes live. No dead links, no fake discounts — just verified savings from {stats.stores}+ brands you already shop.
            </p>

            <form
              onSubmit={submit}
              className="flex items-stretch max-w-2xl border-2 border-slate-950 rounded-sm bg-white"
              data-testid="hero-search-form"
            >
              <div className="flex-1 flex items-center pl-5">
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Try 'nike', 'amazon', or 'free shipping'…"
                  className="flex-1 px-4 py-5 text-base md:text-lg focus:outline-none bg-transparent"
                />
              </div>
              <button
                type="submit"
                className="px-6 md:px-10 bg-slate-950 text-white font-display tracking-tight text-lg hover:bg-emerald-500 hover:text-slate-950 transition-colors"
                data-testid="hero-search-btn"
              >
                Save
              </button>
            </form>

            <div className="flex flex-wrap gap-6 mt-10 text-sm text-slate-600">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Editor-verified</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-emerald-500" /> Updated hourly</span>
              <span className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-500" /> Used 1.2M times</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRENDING BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24" data-testid="trending-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline mb-3">Editor&apos;s picks</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter uppercase">Trending Today</h2>
          </div>
          <Link to="/stores" className="hidden md:inline-flex items-center gap-2 text-sm font-medium link-underline">
            All stores <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {featured.slice(0, 6).map((c) => (
            <CouponCard key={c.id} coupon={c} />
          ))}
        </div>
      </section>

      {/* FEATURED STORES */}
      <section className="border-t border-slate-200 bg-slate-50" data-testid="stores-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline mb-3">Top retailers</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter uppercase">Featured Stores</h2>
            </div>
            <Link to="/stores" className="hidden md:inline-flex items-center gap-2 text-sm font-medium link-underline">
              Browse all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {stores.map((s) => (
              <StoreCard key={s.slug} store={s} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24" data-testid="categories-section">
        <div className="mb-10">
          <p className="overline mb-3">Browse the vault</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter uppercase">Shop by Category</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className="group border border-slate-200 bg-white hover-lift p-6 rounded-sm"
            >
              <p className="overline text-slate-400 mb-3">0{i + 1}</p>
              <h3 className="font-display text-xl tracking-tight text-slate-950 group-hover:text-emerald-600 mb-2">
                {c.name}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">{c.description}</p>
              <div className="mt-6 flex items-center text-sm text-slate-950 gap-2">
                Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-slate-200 bg-slate-950 text-slate-100" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <p className="overline mb-4 text-emerald-400">Why SharePromos</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter uppercase mb-12 max-w-2xl">
            Built for shoppers who hate fake discounts.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: ShieldCheck, title: "Verified daily", body: "Our editors test every code manually before it appears. If it doesn't work, it doesn't stay." },
              { icon: Sparkles, title: "Zero clutter", body: "No pop-ups, no fake countdowns, no email walls. Find the deal, copy the code, save the money." },
              { icon: Users, title: "Community-scored", body: "Real success rates from real shoppers. When a code stops working, you know instantly." },
            ].map((f) => (
              <div key={f.title} className="border-l border-slate-800 pl-6" data-testid={`why-card-${f.title.toLowerCase().replace(/\s+/g,'-')}`}>
                <f.icon className="w-8 h-8 text-emerald-400 mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-2xl tracking-tight mb-3">{f.title}</h3>
                <p className="text-slate-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
